import {
  Component,
  computed,
  inject,
  OnInit,
  Output,
  EventEmitter,
  signal,
} from '@angular/core';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { I18nPipe } from '../../../shared/i18n/i18n-pipe';
import { I18nScopedService } from '../../../shared/i18n/i18n-scoped.service';
import { provideI18nScope } from '../../../shared/i18n/i18n-scope.provider';
import { I18nScopeReadyDirective } from '../../../shared/i18n/i18n-scope-ready.directive';
import { CustomViewsService } from '../../../features/custom-views/services/custom-views.service';
import { CustomViewsUiService } from '../../custom-view/custom-views-ui.service';
import { PermissionService } from '../../../shared/services/permissions/permission.service';
import { Permissions } from '@anessa/shared-models';
import {
  SidebarPopout,
  type SidebarPopoutGroup,
} from '../../sidebar-popout/sidebar-popout';

@Component({
  selector: 'app-custom-views-popout',
  imports: [SidebarPopout, I18nPipe, I18nScopeReadyDirective],
  templateUrl: './custom-views-popout.html',
  providers: [provideI18nScope('ui/sidebar'), I18nScopedService],
})
export class CustomViewsPopout implements OnInit {
  private readonly router = inject(Router);
  private readonly customViewsService = inject(CustomViewsService);
  private readonly customViewsUi = inject(CustomViewsUiService);
  private readonly permissionService = inject(PermissionService, {
    optional: true,
  });

  @Output() readonly popoutClose = new EventEmitter<void>();

  protected readonly activeViewId = signal<string | null>(null);
  protected readonly sortMode = signal<'group' | 'alpha'>('group');

  protected readonly canAdd = computed(
    () =>
      this.permissionService?.hasPermission(Permissions.EDIT_CUSTOM_VIEWS) ??
      false,
  );

  protected readonly groups = computed<SidebarPopoutGroup[]>(() => {
    const views = this.customViewsService.viewList();

    if (this.sortMode() === 'alpha') {
      return this.buildAlphaGroups(views);
    }
    return this.buildGroupGroups(views);
  });

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => this.syncActiveView(e.urlAfterRedirects));
  }

  ngOnInit(): void {
    this.syncActiveView(this.router.url);
  }

  private buildGroupGroups(
    views: { id: string; name: string; group?: string }[],
  ): SidebarPopoutGroup[] {
    const groupMap = new Map<string, { id: string; label: string }[]>();

    for (const view of [...views].sort((a, b) => a.name.localeCompare(b.name))) {
      const key = view.group || '—';
      const items = groupMap.get(key) ?? [];
      items.push({ id: view.id, label: view.name });
      groupMap.set(key, items);
    }

    return Array.from(groupMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, items]) => ({ key, items }));
  }

  private buildAlphaGroups(
    views: { id: string; name: string }[],
  ): SidebarPopoutGroup[] {
    const groupMap = new Map<string, { id: string; label: string }[]>();

    for (const view of [...views].sort((a, b) =>
      a.name.localeCompare(b.name),
    )) {
      const key = view.name[0]?.toUpperCase() ?? '?';
      const items = groupMap.get(key) ?? [];
      items.push({ id: view.id, label: view.name });
      groupMap.set(key, items);
    }

    return Array.from(groupMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, items]) => ({ key, items }));
  }

  private syncActiveView(url: string): void {
    const match = /\/custom-views\/([^/?]+)/.exec(url);
    this.activeViewId.set(match ? decodeURIComponent(match[1]) : null);
  }

  protected toggleSortMode(): void {
    this.sortMode.update((m) => (m === 'group' ? 'alpha' : 'group'));
  }

  protected onItemSelect(id: string): void {
    void this.router.navigate(['/custom-views', id]);
  }

  protected addView(): void {
    this.customViewsUi.openAddViewDialog();
  }
}
